import { Box, Typography } from '@mui/material';
import { EMAIL } from 'src/logic/constants/links';
import LandingLayout from 'src/ui/components/smart/landing/layout/LandingLayout';

const titleSx = { fontWeight: 800, color: '#444444', fontSize: { xs: 14, lg: 18, mb: 1 } };
const bodySx = { fontWeight: 400, color: '#444444', fontSize: { xs: 12, lg: 16 } };

const sections = [
  {
    title: 'Privacy Policy Summary',
  },
  {
    content: (
      <>
        At <strong>eProcurement.ai</strong>, we value your privacy and want you to clearly understand how we use your data:
        <ul style={{ marginBlock: 0, paddingInlineStart: '1.5em' }}>
          <li>
            <strong>Your data, your control-</strong> We collect only the information needed to provide our procurement platform and
            services. You remain in control of your purchasing and approval decisions.
          </li>
          <li>
            <strong>AI-powered, but human-led –</strong> Our platform uses AI in the background to analyse product data, suggest
            alternatives, and generate insights.<br /> The AI is <strong>assistive only</strong> — it never makes binding decisions for you.
          </li>
          <li>
            <strong>Protected & secure –</strong> We apply strict security measures, including encryption, access controls, and continuous
            monitoring, to safeguard your data.
          </li>
          <li>
            <strong>Your rights –</strong> Under DIFC law, you have rights to access, correct, erase, or object to the processing of your
            personal data. You may also contact the DIFC Commissioner of Data Protection if you have concerns.
          </li>
          <li>
            <strong>Our accountability –</strong> We have appointed a <strong>Data Protection Officer (DPO) </strong> and an <strong>Autonomous Systems
              Officer (ASO)</strong> to oversee compliance, maintain an AI Register, and ensure our AI systems are certified by
            January 2026.
          </li>
        </ul>
        <br />
        For any questions, please contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>

        <hr style={{ borderTop: '3px solid #ccc', margin: '30px 0' }} />
      </>
    ),
  },
  {
    title: 'Privacy Policy',
    content: (
      <>
        <br />
        <strong>Effective Date:</strong> September 1, 2025 <br />
        <strong>Entity:</strong> eProcurement Technology Limited, incorporated in the Dubai International Financial Centre (DIFC)
      </>
    ),
  },
  {
    title: '1. Introduction',
    content: (
      <>
        <p>
          eProcurement Technology Limited (“we”, “our”, “us”) is committed to protecting the privacy and personal data of all
          users of our platform. This Privacy Policy explains how we collect, use, share, and safeguard your information in
          accordance with:
        </p>
        <ul style={{ marginBlock: 0 }}>
          <li>
            <strong> DIFC Data Protection Law No. 5 of 2020, </strong>
          </li>
          <li>
            <strong>DIFC Data Protection Regulation 10 (AI Systems),</strong>  and
          </li>
          <li>
            other applicable data protection and privacy regulations.
          </li>
        </ul>
        <p>By accessing or using our services, you agree to the practices described in this Privacy Policy.</p>
      </>
    )
  },
  {
    title: '2. Scope',
    content: (
      <>
        <p>This Policy applies to:</p>
        <ul style={{ marginBlock: 0 }}>
          <li>
            Buyers, sellers, and other users of the eProcurement.ai platform,
          </li>
          <li>
            Visitors to our website,
          </li>
          <li>
            Any individual whose personal data we process in connection with our business.
          </li>
        </ul>
      </>
    )
  },
  {
    title: '3. Personal Data We Collect',
    content: (
      <>
        <p>We may collect the following categories of personal data:</p>
        <ul style={{ marginBlock: 0 }}>
          <li>
            <strong>Identification Data: </strong>  name, job title, company, contact details.
          </li>
          <li>
            <strong>Account Data: </strong> login credentials, user preferences, role permissions.
          </li>
          <li>
            <strong>Transactional Data: </strong>requisitions, RFQs, purchase orders, invoices, communications between buyers and
            sellers.
          </li>
          <li>
            <strong>Technical Data: </strong>device identifiers, IP addresses, usage logs, cookies.
          </li>
          <li>
            <strong>AI-Generated Insights: </strong>product alternatives, supplier matches, recommendations, or analytics generated by AI
            systems.
          </li>
        </ul>
      </>
    )
  },
  {
    title: '4. How We Use Personal Data',
    content: (
      <>
        <p>We process personal data to:</p>
        <ol style={{ marginBlock: 0 }}>
          <li>Provide and operate the eProcurement.ai platform and services.</li>
          <li>Facilitate procurement transactions between buyers and sellers.</li>
          <li>Deliver AI-powered analytics, recommendations, and product or supplier alternatives.</li>
          <li>Improve and enhance our services through monitoring, research, and development.</li>
          <li>Comply with our legal and regulatory obligations under DIFC law.</li>
        </ol>
      </>
    )
  },
  {
    title: '5. Use of AI Systems',
    content: (
      <>
        <p>Our platform uses <strong>AI systems continuously in the background </strong> to collect product information, generate analytics, and
          provide recommendations</p>
        <strong>AI Transparency & Rights</strong>
        <ol style={{ marginBlock: 0 }}>
          <li>AI features are <strong>assistive only </strong>. They do not make binding decisions on behalf of users.</li>
          <li>All procurement and approval decisions remain fully under the control of users.</li>
          <li>Because users remain the decision makers, requests for “human intervention” are not applicable.</li>
          <li>Users retain the right to object to the processing of their personal data by our AI systems where legally
            permissible.</li>
        </ol>
        <br />
        <strong>AI Register</strong>
        <p>We maintain an internal <strong>AI Register</strong> of all AI use cases, as required by Regulation 10.</p>
        <strong>Certification</strong>
        <p>High-risk AI systems deployed commercially will be certified by a DIFC-accredited Certification Body before <strong>January
          2026,</strong> in line with Regulation 10 requirements.</p>
      </>
    )
  },
  {
    title: '6. Legal Basis for Processing',
    content: (
      <>
        <p>We process personal data on the following bases:</p>
        <ul style={{ marginBlock: 0 }}>
          <li>Performance of a contract (e.g., provision of services),</li>
          <li>Compliance with legal and regulatory obligations,</li>
          <li>Legitimate interests (e.g., improving our platform, fraud prevention),</li>
          <li>Consent, where required.</li>
        </ul>
      </>
    )
  },
  {
    title: '7. Data Sharing',
    content: (
      <>
        <p>We may share personal data with:</p>
        <ul style={{ marginBlock: 0 }}>
          <li>Buyers and sellers registered on the platform, as necessary for transactions,</li>
          <li>Service providers supporting our operations (e.g., hosting, analytics, payment processors),</li>
          <li>Regulators, auditors, or authorities, when required by law,</li>
          <li>Other parties with your explicit consent.</li>
        </ul>
        <p>We do not sell personal data to third parties.</p>
      </>
    )
  },
  {
    title: '8. Cross-Border Transfers',
    content: (
      <>
        <p>Where personal data is transferred outside the DIFC, we will ensure that adequate safeguards are in place, consistent
          with <strong>DIFC DP Law 2020,</strong> such as:</p>
        <ul style={{ marginBlock: 0 }}>
          <li>Adequacy decisions,</li>
          <li>Standard contractual clauses,</li>
          <li>Binding corporate rules, or</li>
          <li>Other legally recognised safeguards.</li>
        </ul>
      </>
    )
  },
  {
    title: '9. Data Retention',
    content: (
      <>
        <p>We retain personal data only for as long as necessary to fulfill the purposes outlined in this Policy, and in accordance
          with legal, regulatory, and contractual requirements.</p>
      </>
    )
  },
  {
    title: '10. Security',
    content: (
      <>
        <p>We apply technical and organisational measures to protect personal data, including:</p>
        <ul style={{ marginBlock: 0 }}>
          <li>Encryption of data in transit and at rest,</li>
          <li>Access controls and authentication,</li>
          <li>Monitoring, logging, and incident response procedures,</li>
          <li>Regular testing of AI models for fairness, accuracy, and resilience.</li>
        </ul>
      </>
    )
  },
  {
    title: '11. Your Rights',
    content: (
      <>
        <p>Under DIFC DP Law and Regulation 10, you have the following rights:</p>
        <ul style={{ marginBlock: 0 }}>
          <li>Right of access to your personal data,</li>
          <li>Right to rectification of inaccurate or incomplete data,</li>
          <li>Right to erasure (“right to be forgotten”),</li>
          <li>Right to object to or restrict processing, including AI-driven processing,</li>
          <li>Right to data portability,</li>
          <li>Right to lodge a complaint with the DIFC Commissioner of Data Protection.</li>
        </ul>
        <p> Requests may be submitted by contacting us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> <br /></p>
      </>
    )
  },
  {
    title: '12. Roles & Accountability',
    content: (
      <>
        <p>We have appointed both a <strong> Data Protection Officer (DPO)</strong>  and an <strong> Autonomous Systems Officer (ASO),</strong>  in line with
          DIFC legal requirements.</p>
        <p>These officers oversee:</p>
        <ul style={{ marginBlock: 0 }}>
          <li>Compliance with DIFC DP Law and Regulation 10,</li>
          <li>Maintenance of the AI Register,</li>
          <li>Certification of AI systems,</li>
          <li>Acting as the contact point for regulators and data subjects.</li>
        </ul>
        <p> Contact <a href={`mailto:${EMAIL}`}>{EMAIL}</a> <br /></p>
      </>
    )
  },
  {
    title: '13. Updates to this Policy',
    content: (
      <>
        <p>We may update this Privacy Policy from time to time to reflect changes in law, regulation, or our practices. Updates
          will be published on our website, with the effective date noted above.</p>
        <hr style={{ borderTop: '3px solid #ccc', margin: '30px 0' }} />
      </>
    )
  },
  {
    title: '14. Contact Us',
    content: (
      <>
        For any questions, concerns, or to exercise your rights, please contact: <a href={`mailto:${EMAIL}`}>{EMAIL}</a><br />
        You also have the right to contact the <strong> DIFC Commissioner of Data Protection </strong> if you believe your rights have been
        infringed.
      </>
    )
  },
  {
    title: '15. Cookie Notice',
    content: (
      <>
        <span className='anchor' id='cookie-notice'></span>
        <p><strong>What are cookies?</strong></p>
        <p>Cookies are small text files placed on your device when you visit a website. They help us recognise your browser,
          remember your preferences, and understand how you interact with our platform.</p>
        <p><strong>Types of cookies we use</strong></p>
        <ul style={{ marginBlock: 0 }}>
          <li> <strong>Essential cookies –</strong> Required for the platform to function (e.g., login, session management, security).</li>
          <li> <strong>Performance & analytics cookies –</strong> Help us understand usage patterns and improve services (e.g., which
            pages are most visited).</li>
          <li> <strong>Preference cookies –</strong> Remember your settings and choices (e.g., language, saved preferences).</li>
          <li> <strong>Third-party cookies –</strong> Certain third parties (e.g., analytics providers) may place cookies to help us measure
            engagement.
          </li>
        </ul>
        <p><strong>How you can control cookies</strong></p>
        <ul style={{ marginBlock: 0 }}>
          <li>Most browsers allow you to manage or block cookies in settings.</li>
          <li>If you disable cookies, some features of the platform may not work as intended.</li>
        </ul>
        <p><strong>Legal basis</strong></p>
        <p>We use cookies where they are <strong> necessary for the performance of our services.</strong> For other cookies (analytics,
          preferences), we rely on <strong>your consent.</strong>
        </p>
        <p><strong>Further information</strong></p>
        If you have questions about our use of cookies, please contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
      </>
    )
  }
];

const PrivacyPolicyPage = () => {
  return (
    <div className='page'>
      <Box className='page__content' py={6}>
        <Typography fontWeight={600} color={'#444444'} fontSize={{ xs: 30, lg: 48 }}>
          Privacy Policy
        </Typography>

        <Box sx={{ py: 2 }}>
          {sections.map(({ title, content }, idx) => (
            <Box key={idx} mb={4} >
              <Typography variant='h6' component='h2' sx={titleSx}>
                {title}
              </Typography>
              <Typography variant='body1' sx={bodySx} paragraph>
                {content}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </div>
  );
};

PrivacyPolicyPage.getLayout = function getLayout(page: React.ReactElement) {
  return <LandingLayout>{page}</LandingLayout>;
};

export default PrivacyPolicyPage;
